import React from 'react';
import {
  TextField,
  Slider,
  Box,
  Button,
  Chip,
  Stack,
  Rating,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { formFieldStyles } from './FormField.styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { trTR, enUS as muiEnUS } from '@mui/x-date-pickers/locales';
import { parseDateForForm } from '../../utils/dateUtils';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const AdvancedField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  formData,
}) => {
  const { t, i18n } = useTranslation();
  const localeText = i18n.language === 'tr'
    ? {
        ...trTR.components.MuiLocalizationProvider.defaultProps.localeText,
        okButtonLabel: t('leaves.datepicker.ok', 'Tamam'),
        cancelButtonLabel: t('leaves.datepicker.cancel', 'İptal'),
      }
    : {
        ...muiEnUS.components.MuiLocalizationProvider.defaultProps.localeText,
        okButtonLabel: t('leaves.datepicker.ok', 'OK'),
        cancelButtonLabel: t('leaves.datepicker.cancel', 'Cancel'),
      };

  const handleChange = (event, newValue) => {
    let changedValue;

    if (field.type === 'file') {
      changedValue = event.target.files;
    } else if (field.type === 'range' || field.type === 'rating') {
      changedValue = newValue;
    } else if (field.type === 'tags') {
      changedValue = newValue;
    } else {
      changedValue = event.target.value;
    }

    if (onChange) {
      onChange(field.name, changedValue, event);
    }
  };

  const handleBlur = event => {
    if (onBlur) {
      onBlur(field.name, value, event);
    }
  };

  const baseProps = {
    disabled,
    error: !!error,
    fullWidth: true,
    sx: formFieldStyles.field,
  };

  switch (field.type) {
    case 'color':
      return (
        <FormControl {...baseProps}>
          <FormLabel component="legend">{t(field.label)}</FormLabel>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <input
              type="color"
              value={value || '#000000'}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              style={{
                width: '60px',
                height: '40px',
                border: 'none',
                borderRadius: '4px',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            />
            <TextField
              value={value || '#000000'}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={field.placeholder || '#000000'}
              disabled={disabled}
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      );

    case 'range':
      return (
        <FormControl {...baseProps}>
          <FormLabel component="legend">{t(field.label)}</FormLabel>
          <Box sx={{ px: 2, mt: 2 }}>
            <Slider
              value={value || field.min || 0}
              onChange={(event, newValue) => handleChange(event, newValue)}
              onBlur={handleBlur}
              disabled={disabled}
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
              marks={field.marks}
              valueLabelDisplay="auto"
              valueLabelFormat={field.valueLabelFormat || (val => val)}
            />
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      );

    case 'file':
      return (
        <FormControl {...baseProps}>
          <FormLabel component="legend">{t(field.label)}</FormLabel>
          <Box sx={{ mt: 1 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={disabled}
              sx={{ mb: 1 }}
            >
              {field.placeholder || 'Dosya Seç'}
              <VisuallyHiddenInput
                type="file"
                onChange={handleChange}
                onBlur={handleBlur}
                multiple={field.multiple}
                accept={field.accept}
              />
            </Button>
            {value && value.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Array.from(value).map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      );

    case 'rating':
      return (
        <FormControl {...baseProps}>
          <FormLabel component="legend">{t(field.label)}</FormLabel>
          <Box sx={{ mt: 1 }}>
            <Rating
              value={value || 0}
              onChange={(event, newValue) => handleChange(event, newValue)}
              onBlur={handleBlur}
              disabled={disabled}
              max={field.max || 5}
              precision={field.precision || 1}
              emptyIcon={
                <StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />
              }
            />
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      );

    case 'tags':
      return (
        <FormControl {...baseProps}>
          <FormLabel component="legend">{field.label ? t(field.label) : ''}</FormLabel>
          <Box sx={{ mt: 1 }}>
            <TextField
              placeholder={field.placeholder ? t(field.placeholder) : "Tag ekleyin ve Enter'a basın"}
              disabled={disabled}
              onKeyPress={e => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  e.preventDefault();
                  const newTag = e.target.value.trim();
                  const currentTags = Array.isArray(value) ? value : [];
                  if (!currentTags.includes(newTag)) {
                    handleChange(null, [...currentTags, newTag]);
                  }
                  e.target.value = '';
                }
              }}
              fullWidth
              size="small"
            />
            {Array.isArray(value) && value.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {value.map((tag, index) => {
                  const label = typeof tag === 'string' ? tag : (tag?.name ?? String(tag));
                  return (
                    <Chip
                    onClick={() => {}}
                      key={index}
                      label={label}
                      variant="outlined"
                      size="small"
                      onDelete={() => {
                        const newTags = value.filter((_, i) => i !== index);
                        handleChange(null, newTags);
                      }}
                    />
                  );
                })}
                </Stack>
              </Box>
            )}
          </Box>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      );

    case 'time':
      return (
        <TextField
          {...baseProps}
          type="time"
          label={field.label}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'datetime-local': {
      // Saat / dakika görünürlüğünü dinamik kontrol et
      let showTime = true;
      if (typeof field.showTime === 'function') {
        showTime = field.showTime(formData || {});
      } else if (typeof field.showTime === 'boolean') {
        showTime = field.showTime;
      }

      const views = showTime
        ? ['year', 'month', 'day', 'hours', 'minutes']
        : ['year', 'month', 'day'];
      const format = showTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy';

      // Modern, beautiful DateTimePicker with theme and localized buttons
      return (
        <ThemeProvider theme={createTheme({
          components: {
            MuiPickersDay: {
              styleOverrides: {
                root: {
                  fontWeight: 600,
                  borderRadius: 8,
                  border: 'none',
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                  },
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                  },
                },
                today: {
                  border: '2px solid #1976d2',
                },
              },
            },
            MuiPickersCalendarHeader: {
              styleOverrides: {
                root: {
                  background: '#f5f7fa',
                  borderRadius: 8,
                  marginBottom: 8,
                },
                label: {
                  fontWeight: 700,
                  fontSize: 18,
                },
              },
            },
            MuiPaper: {
              styleOverrides: {
                root: {
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
              },
            },
          },
        })}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={field.locale || (i18n.language === 'tr' ? tr : enUS)}
            localeText={localeText}
          >
            <DateTimePicker
              label={t(field.label)}
              value={parseDateForForm(value)}
              onChange={newValue => onChange(field.name, newValue)}
              onError={handleBlur}
              disabled={disabled}
              views={views}
              format={format}
                              slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    sx: formFieldStyles.field,
                  },
                actionBar: {
                  actions: ['cancel', 'accept'],
                },
              }}
        />
          </LocalizationProvider>
        </ThemeProvider>
      );
    }

    case 'month':
      return (
        <TextField
          {...baseProps}
          type="month"
          label={field.label}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'week':
      return (
        <TextField
          {...baseProps}
          type="week"
          label={field.label}
          value={value || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          InputLabelProps={{ shrink: true }}
        />
      );

    default:
      return null;
  }
};

export default AdvancedField;
